'use client'

import React from 'react'
import { usePathname } from 'next/navigation'
import { SessionProvider } from 'next-auth/react'
import { I18nProvider } from '@/i18n/client'
import { TranslationNotice } from '@/components/ui/translation-notice'
import { CookieBanner } from '@/components/legal/cookie-banner'
import { PrivacyReConsentModal } from '@/components/auth/privacy-reconsent-modal'
import { WaiverGate } from '@/components/legal/waiver-gate'
import { FloatingTooltip } from '@/components/ui/floating-tooltip'
import { FglFooter } from '@/components/layout/fgl-footer'
import { PresenceHeartbeat } from '@/components/presence/presence-heartbeat'
import { WertungMaintenanceModal } from '@/components/wertung/wertung-maintenance-modal'
import Script from 'next/script'
import dynamic from 'next/dynamic'
import { GlobalJsonLd } from '@/components/seo/global-jsonld'
import { HreflangAlternates } from '@/components/seo/hreflang-alternates'
import { BreadcrumbsJsonLd } from '@/components/seo/breadcrumbs-jsonld'

// Cirra + Chat-Widget client-only laden — die Render-Bedingungen ueber
// useSession() und usePathname() schwanken zwischen Server (unauth) und
// Client (auth) und verursachen sonst React-Hydration-Mismatches auf der
// Homepage. ssr:false rendert sie ausschliesslich nach dem Mount.
const ChatWidgetMount = dynamic(
  () => import('@/components/ai/chat-widget-mount').then(m => m.ChatWidgetMount),
  { ssr: false }
)
const CirraWidget = dynamic(
  () => import('@/components/cirra/cirra-widget').then(m => m.CirraWidget),
  { ssr: false }
)
const ProactiveHelp = dynamic(
  () => import('@/components/cirra/proactive-help').then(m => m.ProactiveHelp),
  { ssr: false }
)
// r19.55.225 (2026-08-14): Neue AppBottomNav mit 5 Slots + Puck ersetzt den
// klassischen EdgeRail (der bis 1024 px als reiner Puck aktiv war). Neue Nav
// aktiviert bis 1279 px (Handy + Tablet + kleine Laptops). Slots:
// Home / Fluege | Puck-Mehr | Upload / Wertung.
const AppBottomNav = dynamic(
  () => import('@/components/layout/app-bottom-nav').then(m => m.AppBottomNav),
  { ssr: false }
)
// GlobalAppSidebar: rendert die AppSidebar global auf Public + /app (NICHT
// auf /admin — dort übernimmt der AdminQuickNavPuck oben).
const GlobalAppSidebar = dynamic(
  () => import('@/components/layout/global-app-sidebar').then(m => m.GlobalAppSidebar),
  { ssr: false }
)
// AdminQuickNavPuck: kleine FGL-Glass-Anchor-Puck oben mittig auf /admin.
// Klick → Glass-Sheet slidet von oben mit App+Public Quick-Nav. Konflikt mit
// AdminSidebar links wird vermieden, weil die Puck oben mittig sitzt.
const AdminQuickNavPuck = dynamic(
  () => import('@/components/layout/admin-quick-nav-puck').then(m => m.AdminQuickNavPuck),
  { ssr: false }
)
// R106.4 (2026-06-08, Stephan-Spec): Bottom-Ticker im welt.de-Stil.
// EILT-Meldungen mit Zeitfenster + Teaser aus letzten News.
const BreakingNewsTicker = dynamic(
  () => import('@/components/layout/breaking-news-ticker').then(m => m.BreakingNewsTicker),
  { ssr: false }
)
import './globals.css'

// Inline script das VOR dem Rendern ausgefuehrt wird — kein FOUC.
// Setzt IMMER ein data-theme (default 'start'), damit es nicht spaeter von
// React-Re-Renders des <html>-Elements auf 'start' zurueckgesetzt wird.
const themeScript = `
(function(){
  try {
    var V = ['start','schwacheraufwind','aufkurs','aussenlandung','gleiten','ueberland','ruppig','starkethermik','endanflug','abschlussbier','thermik','speed'];
    var LEGACY = {cumulus:'start',morgenrot:'schwacheraufwind',himmelsstuermer:'aufkurs',goodlandings:'aussenlandung',bluesky:'gleiten',suedafrika:'ueberland',hangwind:'ruppig',abendbart:'starkethermik',seewind:'endanflug',waldkante:'abschlussbier',thermik:'thermik',nachtflug:'speed'};
    var t = localStorage.getItem('fgl-theme');
    var target = 'start';
    if (t && V.indexOf(t) !== -1) {
      target = t;
    } else if (t && LEGACY[t]) {
      target = LEGACY[t];
      localStorage.setItem('fgl-theme', target);
    }
    document.documentElement.setAttribute('data-theme', target);
  } catch(e) { document.documentElement.setAttribute('data-theme', 'start'); }
})();
`

// R90.3 (2026-05-18): Stale-Bundle-Killer. Erkennt typische Browser-Errors
// die auf einen veralteten JS-Bundle hinweisen (ChunkLoadError, Loading
// chunk failed, Unexpected token "<") UND deren Häufung (>= 3 in 30 s) und
// fuehrt dann einen Hard-Reset durch (SW unregistrieren, Caches loeschen,
// reload mit Cache-Buster). Laeuft komplett ausserhalb von React — fängt
// auch Fehler ab die garnicht erst zum Error-Boundary durchkommen.
const staleBundleKillerScript = `
(function(){
  try {
    var KEY = 'fgl_crash_log_v1', W = 30000, LIMIT = 3;
    function isStaleBundle(msg) {
      if (!msg) return false;
      var s = String(msg);
      // r19.55.95 (2026-08-06): "Connection closed." + "Failed to load chunk"
      // + "Failed to fetch" ergaenzt. Konkreter Fall: 9 Auto-Reports vom
      // Chunk 05f_5nzcc6vub.js:1:14209 nach Deploy — Browser mit altem HTML
      // versucht alten Chunk-Hash zu laden, Server 404, HTTP/2-Stream
      // schliesst → "Connection closed." statt "ChunkLoadError". Selber
      // Root-Cause, andere Fehler-Message.
      return s.indexOf('ChunkLoadError') >= 0
          || s.indexOf('Loading chunk') >= 0
          || s.indexOf('Failed to load chunk') >= 0
          || s.indexOf('Failed to fetch dynamically imported module') >= 0
          || s.indexOf('Failed to fetch') >= 0
          || s.indexOf('Connection closed.') >= 0
          || s.indexOf('Unexpected token \\'<\\'') >= 0
          || s.indexOf('Minified React error #300') >= 0
          || s.indexOf('Minified React error #418') >= 0
          || s.indexOf('Minified React error #423') >= 0
          || s.indexOf('Minified React error #425') >= 0;
    }
    async function hardReset() {
      // R100.3 (2026-05-25) Loop-Schutz: wenn URL bereits _hard_reset hat und
      // der letzte Reset < 60 s her ist → das ist KEIN stale-Bundle-Problem
      // (sonst waere der Reload schon erfolgreich gewesen), sondern ein echter
      // Code-Bug. Weiteres Hard-Resetten wuerde nur eine Endlos-Schleife
      // erzeugen — Browser-Tab klemmt, User kommt nicht mehr weg.
      // Stephan-Beobachtung 2026-05-25: /tools/eagle-download?_hard_reset=...
      // loeste Loop aus.
      try {
        var existingResetParam = new URL(window.location.href).searchParams.get('_hard_reset');
        if (existingResetParam) {
          var existingMs = parseInt(existingResetParam, 10);
          if (existingMs && Date.now() - existingMs < 60000) {
            console.error('[fgl] crash-loop detected (' + Math.round((Date.now() - existingMs)/1000) + 's after last reset) — stopping hard-reset cycle. Code-bug, not stale-bundle.');
            try { localStorage.removeItem(KEY); } catch(e){}
            return;
          }
        }
      } catch(e){}
      try {
        if ('serviceWorker' in navigator) {
          var regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(function(r){ return r.unregister().catch(function(){}); }));
        }
      } catch(e){}
      try {
        if ('caches' in window) {
          var keys = await caches.keys();
          await Promise.all(keys.map(function(k){ return caches.delete(k).catch(function(){}); }));
        }
      } catch(e){}
      try { sessionStorage.clear(); } catch(e){}
      try { localStorage.removeItem(KEY); } catch(e){}
      var url = new URL(window.location.href);
      url.searchParams.set('_hard_reset', String(Date.now()));
      window.location.replace(url.toString());
    }
    function track(msg) {
      try {
        var now = Date.now();
        var arr = JSON.parse(localStorage.getItem(KEY) || '[]');
        arr = arr.filter(function(t){ return now - t < W; });
        arr.push(now);
        localStorage.setItem(KEY, JSON.stringify(arr));
        // Stale-Bundle UND >= LIMIT in 30 s → sofort Hard-Reset
        if (isStaleBundle(msg) || arr.length >= LIMIT) hardReset();
      } catch(e){}
    }
    window.addEventListener('error', function(e){
      track(e && (e.message || (e.error && e.error.message)));
    });
    window.addEventListener('unhandledrejection', function(e){
      track(e && e.reason && (e.reason.message || String(e.reason)));
    });
  } catch(e){}
})();
`

export default function RootLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const skipFooter = pathname.startsWith('/app') || pathname.startsWith('/admin')
    || pathname === '/under-construction' || pathname === '/offline' || pathname === '/maintenance'

  // Umami-Analytics nur einbinden, wenn die Website-ID gesetzt ist und der
  // User nicht im Admin-Bereich ist. Tracker laeuft ueber Server-Proxy
  // (/api/analytics/f.js + /api/analytics/send) — DSGVO-konform, eigene Domain.
  const umamiWebsiteId = process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID
  const enableUmami = !!umamiWebsiteId && !pathname.startsWith('/admin')

  return (
    <html lang="de" translate="no" suppressHydrationWarning>
      <head>
        {/* Browser-Translation-Extensions (Google Translate, DeepL) mutieren das
            DOM ausserhalb von React und zerschiessen den Reconciler →
            "Failed to execute 'removeChild' on 'Node'"-Crash. Site hat eigene
            i18n (17 Locales via I18nProvider), Browser-Translate ist daher
            redundant. translate="no" + Meta-Tag = Defense-in-Depth.
            Ref: facebook/react#11538, R102.11 Atlas-Mitigation, Bug-Reports
            cmpwtpve400222s2updttvczn (David Horgan), 11 weitere Crash-Pfade. */}
        <meta name="google" content="notranslate" />
        <script dangerouslySetInnerHTML={{ __html: themeScript }} />
        <script dangerouslySetInnerHTML={{ __html: staleBundleKillerScript }} />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="theme-color" content="#8a5300" media="(prefers-color-scheme: light)" /> {/* 2026-06-19 Kontrast-Sweep: #d99b1c → #8a5300 (CB-safe) */}
        <meta name="theme-color" content="#0a0a0a" media="(prefers-color-scheme: dark)" />
        {/* R90.3: 'mobile-web-app-capable' ist der moderne Standard, apple-* bleibt
            für iOS-Kompatibilitaet — Chrome warnt sonst in der Console. */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="FGL" />
        <meta name="application-name" content="FREE GLIDE LEAGUE" />
        <meta name="description" content="FREE GLIDE LEAGUE — die offizielle Serie des Markeninhabers Stephan Eich fuer RC-Modellsegelflug, unabhaengig von FAI. Fluganalyse-Plattform mit IGC-Upload, Wertung, Rankings und Community." />
        {/* r19.55.207 Origin-Autoritaets-Beweise: Suchmaschinen sollen
            free-glide-league.com als EINZIGE Original-Quelle der FREE GLIDE
            LEAGUE erkennen — nicht Kopien auf rcmodelspot.com oder anderen
            Portalen. Signale: author + publisher + copyright + reply-to +
            owner + originator alle kongruent auf Stephan Eich / Modellpilot.EU. */}
        <meta name="author" content="Stephan Eich" />
        <meta name="publisher" content="Modellpilot.EU (Stephan Eich)" />
        <meta name="copyright" content='© 2023 Stephan Eich, Berlin/Germany (Regelwerk-Urheberrecht) · DPMA-Wortmarke "FREE GLIDE LEAGUE" seit 2026 · Plattform-Gruendung 2024 · Alle Rechte vorbehalten. Nutzung, Kopie oder Weitergabe — als Ganzes oder in Teilen — nur mit ausdruecklichem schriftlichem Einverstaendnis. Lizenzen fuer Ganz- oder Teilnutzung koennen erworben werden. Kontakt: info@modellpilot.eu' />
        <meta name="owner" content="Stephan Eich / Modellpilot.EU" />
        <meta name="originator" content="Stephan Eich / Modellpilot.EU" />
        <meta name="reply-to" content="info@modellpilot.eu" />
        <meta name="designer" content="Stephan Eich" />
        <meta name="rights" content="All rights reserved. Use, reproduction, or distribution — in whole or in part — only with express written consent of Stephan Eich. Licenses for full or partial use are available on request. Contact: info@modellpilot.eu" />
        {/* Dublin Core — akademisch etabliert, wird von semantischen Crawlern gelesen. */}
        <meta name="DC.creator" content="Stephan Eich" />
        <meta name="DC.publisher" content="Modellpilot.EU (Stephan Eich)" />
        <meta name="DC.rights" content="© 2023 Stephan Eich (Regelwerk) · DPMA-Wortmarke seit 2026 · Plattform-Gruendung 2024. Alle Rechte vorbehalten. Nutzung — als Ganzes oder in Teilen — nur mit schriftlichem Einverstaendnis. Lizenzen erwerbbar. Kontakt: info@modellpilot.eu" />
        <meta name="DC.rightsHolder" content="Stephan Eich" />
        <meta name="DC.source" content="https://free-glide-league.com" />
        <meta name="DC.identifier" content="https://free-glide-league.com" />
        <meta name="DC.language" content="de" />
        <meta name="DC.type" content="Service" />
        <meta name="DC.coverage" content="Worldwide" />
        {/* Robots — explizit alles erlauben (Original-Quelle soll indexiert werden). */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="bingbot" content="index, follow" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/icons/icon-192x192.png" />
        {/* Open Graph / Facebook + WhatsApp + Telegram + LinkedIn-Preview.
            Ohne diese Tags sucht Facebook selbst nach einem Bild auf der Seite und
            findet im schlimmsten Fall die DE-Flagge aus dem Sprachwähler. og:image
            MUSS eine absolute URL sein — relative URLs werden von Facebook ignoriert. */}
        <meta property="og:site_name" content="FREE GLIDE LEAGUE" />
        <meta property="og:title" content="FREE GLIDE LEAGUE" />
        <meta property="og:description" content="FREE GLIDE LEAGUE — die offizielle Serie des Markeninhabers Stephan Eich fuer RC-Modellsegelflug, unabhaengig von FAI." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free-glide-league.com/" />
        <meta property="og:image" content="https://free-glide-league.com/icons/icon-512x512.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content="FREE GLIDE LEAGUE Logo" />
        <meta property="og:locale" content="de_DE" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FREE GLIDE LEAGUE" />
        <meta name="twitter:description" content="FREE GLIDE LEAGUE — die offizielle Serie des Markeninhabers Stephan Eich fuer RC-Modellsegelflug, unabhaengig von FAI." />
        <meta name="twitter:image" content="https://free-glide-league.com/icons/icon-512x512.png" />
        <meta name="twitter:image:alt" content="FREE GLIDE LEAGUE Logo" />
        {/* Webfonts werden lokal aus /public/fonts/ geladen (siehe globals.css @font-face).
            Kein Drittanbieter-CDN zur Laufzeit — DSGVO-konform (LG München I 2022, Az. 3 O 17493/20). */}
        {/* r19.55.203 GEO-Infra: canonical + hreflang-Alternates fuer 17 Sprachen */}
        <HreflangAlternates />
        {/* r19.55.203 GEO-Infra: SportsOrganization + WebSite JSON-LD (global) */}
        <GlobalJsonLd />
        {/* r19.55.203 GEO-Infra: BreadcrumbList JSON-LD (auto aus Pathname, nicht auf Home) */}
        <BreadcrumbsJsonLd />
      </head>
      <body>
        <SessionProvider>
          <I18nProvider>
            {children}
            {!skipFooter && <FglFooter />}
            <TranslationNotice />
            <CookieBanner />
            <PrivacyReConsentModal />
            <WaiverGate />
            <FloatingTooltip />
            {/* R102.2 (Stephan-Wunsch 2026-05-31): altes ChatWidget abgeschaltet —
                Stephan hat das Smiley-Trigger-Icon (FG-Bubble) als Freeglidy
                erkannt, war aber das ALTE Widget. CirraWidget(freeglidy) übernimmt
                jetzt vollständig — gleiches Design wie Cirra, nur Endpoint+Storage
                anders. */}
            {/* <ChatWidgetMount /> */}
            {/* R102 (Stephan-Spec 2026-05-31): Cirra (public, Sekretärin) +
                Freeglidy (logged-in im /app/, ersetzt Cirra). Beide Widgets
                rendern aber zeigen sich nur auf den passenden Pfaden via
                shouldShowOnPath(pathname, botKind). */}
            <CirraWidget botKind="cirra" />
            <CirraWidget botKind="freeglidy" />
            {/* R102 (P17): Proaktive Hilfe — dezent, mit Cooldown, DSGVO-light. */}
            <ProactiveHelp />
            <AppBottomNav />
            <GlobalAppSidebar />
            <AdminQuickNavPuck />
            {/* R100.56 (2026-05-30): Online-Status-Heartbeat. Aktualisiert
                users.last_seen_at alle 60s wenn Tab fokussiert + eingeloggt. */}
            <PresenceHeartbeat />
            <BreakingNewsTicker />
            {/* R106.4.11: Wartungs-Modal — erscheint bei Antrags-Aktionen wenn aktiv */}
            <WertungMaintenanceModal />
          </I18nProvider>
        </SessionProvider>
        {enableUmami && (
          <Script
            src="/api/analytics/f.js"
            strategy="afterInteractive"
            data-website-id={umamiWebsiteId}
            data-host-url="/api/analytics"
          />
        )}
      </body>
    </html>
  )
}
